/** Generic list page: renders a list with search/order, opens a drawer to edit a single item. */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Save,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const ConfirmDialog = ({ open, title, onCancel, onConfirm }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/60 backdrop-blur-md"
      >
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass rounded-2xl p-6 max-w-sm w-full"
          data-testid="admin-confirm-dialog"
        >
          <h3 className="font-display text-lg text-white">Are you sure?</h3>
          <p className="mt-2 text-sm text-slate-400">{title}</p>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              data-testid="admin-confirm-cancel"
              className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              data-testid="admin-confirm-ok"
              className="px-4 py-2 rounded-full text-sm bg-red-500 text-white hover:bg-red-400"
            >
              Delete
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Drawer = ({ open, onClose, title, children, onSave, saving, testId }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex"
        data-testid={testId || "admin-drawer"}
      >
        <div className="flex-1 bg-black/60 backdrop-blur-md" onClick={onClose} />
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 280, damping: 32 }}
          className="w-full max-w-xl h-screen bg-[#06060c] border-l border-white/10 overflow-y-auto flex flex-col"
        >
          <div className="sticky top-0 bg-[#06060c]/95 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-white/5 z-10">
            <h3 className="font-display text-lg text-white truncate">{title}</h3>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="admin-drawer-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-medium disabled:opacity-70"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="p-6 space-y-5 flex-1">{children}</div>
          <div className="sticky bottom-0 px-6 py-3 bg-[#06060c]/95 backdrop-blur border-t border-white/5 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="admin-drawer-save-bottom"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-medium disabled:opacity-70"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </motion.aside>
      </motion.div>
    )}
  </AnimatePresence>
);

const ListEditor = ({
  resource, // adminApi.<resource>
  resourceKey, // "projects" etc.
  title,
  description,
  newItem, // fn returning blank entity
  renderRow, // (item) => row content (left side)
  renderForm, // (draft, setField) => fields jsx
  searchFields = ["title", "name", "company", "school"],
  canDuplicate = false,
  canReorder = true,
}) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const refresh = async () => {
    const list = await resource.list();
    setItems(list);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((it) =>
      searchFields.some((f) => (it[f] ?? "").toString().toLowerCase().includes(needle))
    );
  }, [items, q, searchFields]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (draft.id) {
        await resource.update(draft.id, draft);
      } else {
        await resource.create(draft);
      }
      toast.success("Saved");
      setDraft(null);
      await refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((x) => x.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= items.length) return;
    const list = [...items];
    const a = list[idx];
    const b = list[swap];
    [a.order, b.order] = [b.order, a.order];
    list.sort((x, y) => x.order - y.order);
    setItems(list);
    try {
      await resource.reorder(list.map((x, i) => ({ id: x.id, order: i })));
    } catch {
      toast.error("Reorder failed");
      refresh();
    }
  };

  const toggleVisible = async (item) => {
    try {
      await resource.update(item.id, { ...item, visible: !item.visible });
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const duplicate = async (id) => {
    try {
      await resource.duplicate(id);
      toast.success("Duplicated");
      refresh();
    } catch {
      toast.error("Duplicate failed");
    }
  };

  return (
    <div className="space-y-5" data-testid={`admin-list-${resourceKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-1">CMS</div>
          <h1 className="font-display text-3xl text-white font-medium">{title}</h1>
          {description && <p className="text-slate-400 text-sm mt-1">{description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 glass rounded-full px-3 py-2">
            <Search size={14} className="text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              data-testid={`admin-search-${resourceKey}`}
              className="bg-transparent text-sm text-white placeholder:text-slate-600 focus:outline-none w-40"
            />
          </div>
          <button
            onClick={() => setDraft(newItem())}
            data-testid={`admin-new-${resourceKey}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black text-sm font-medium hover:shadow-[0_0_30px_rgba(0,240,255,0.35)] transition-shadow"
          >
            <Plus size={14} /> New
          </button>
        </div>
      </div>

      {items === null && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="glass rounded-xl p-4 animate-pulse h-16" />
          ))}
        </div>
      )}

      {items !== null && filtered.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-400 text-sm">
          Nothing here yet. Click <span className="text-cyan-300">New</span> to add one.
        </div>
      )}

      <div className="space-y-2" data-testid={`admin-rows-${resourceKey}`}>
        {filtered.map((it, i) => (
          <motion.div
            key={it.id}
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-xl p-4 flex items-center justify-between gap-4"
            data-testid={`admin-row-${resourceKey}-${i}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="font-mono text-[10px] text-slate-500 w-6 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">{renderRow(it)}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canReorder && (
                <>
                  <button
                    onClick={() => move(it.id, -1)}
                    className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                    title="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => move(it.id, 1)}
                    className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                    title="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                </>
              )}
              <button
                onClick={() => toggleVisible(it)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                title={it.visible ? "Hide" : "Show"}
              >
                {it.visible ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              {canDuplicate && (
                <button
                  onClick={() => duplicate(it.id)}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
                  title="Duplicate"
                >
                  <Copy size={14} />
                </button>
              )}
              <button
                onClick={() => setDraft(it)}
                data-testid={`admin-edit-${resourceKey}-${i}`}
                className="p-2 text-cyan-300 hover:text-white rounded-lg hover:bg-white/5"
                title="Edit"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => setToDelete(it)}
                data-testid={`admin-delete-${resourceKey}-${i}`}
                className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-white/5"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <Drawer
        open={!!draft}
        title={draft?.id ? `Edit ${title.slice(0, -1)}` : `New ${title.slice(0, -1)}`}
        onClose={() => setDraft(null)}
        onSave={save}
        saving={saving}
      >
        {draft && renderForm(draft, setField)}
      </Drawer>

      <ConfirmDialog
        open={!!toDelete}
        title={`This will permanently delete this item.`}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          try {
            await resource.remove(toDelete.id);
            toast.success("Deleted");
            setToDelete(null);
            refresh();
          } catch {
            toast.error("Delete failed");
          }
        }}
      />
    </div>
  );
};

export default ListEditor;
